import { v4 } from 'uuid';

import { ModuleDTO, PlanDTO } from '../api/module.api';
import {
  Syllabus,
  QuizMeta,
  Module,
  SyllabusStats,
  ModuleStats,
  Video,
  ModulePrice,
  SyllabusSection,
  Plan,
} from '../model/types';

export const parseModuleDTO = (dto: ModuleDTO, plan?: PlanDTO, isPlan?: boolean): Module => {
  return {
    id: dto.id,
    title: dto.title,
    description: dto.description,
    level: dto.level,
    syllabus:
      dto.syllabus
        ?.map(
          (s) =>
            ({
              id: s.id || v4(),
              title: s.title,
              order: s.order,
              sections: isPlan
                ? s.sections_titles_sorted?.map(
                    (t) =>
                      ({
                        title: t,
                        completed: false,
                      }) as SyllabusSection,
                  ) || []
                : s.sections_titles_with_status || [],
              stats: {
                totalCount: s.stats?.total_count || 0,
                completedCount: s.stats?.completed_count || 0,
                completed: s.stats?.completed || false,
              } as SyllabusStats,
            }) as Syllabus,
        )
        .sort((a, b) => a.order - b.order) || [],
    quizzes:
      dto.quizes
        ?.map(
          (q) =>
            ({
              id: q.id || v4(),
              title: q.title,
              order: q.order,
              completed: q.completed || false,
              failed: q.failed || false,
              score: q.score || 0,
              locked: q.locked || false,
            }) as QuizMeta,
        )
        .sort((a, b) => a.order - b.order) || [],
    ownerId: dto.owner_id || '',
    rating: dto.rating || 0,
    createdAt: dto.created_at || '',
    updatedAt: dto.updated_at || '',
    imageUrl: dto.image_url,
    price: dto.price
      ? ({
          priceId: dto.price.price_id,
          amount: dto.price.amount,
          currency: dto.price.currency,
          region: dto.price.region,
          taxAmount: dto.price.tax_amount,
          taxPercentage: dto.price.tax_percentage,
        } as ModulePrice)
      : undefined,
    subscriptionPrice: dto.subscription_price
      ? ({
          priceId: dto.subscription_price.price_id,
          amount: dto.subscription_price.amount,
          currency: dto.subscription_price.currency,
          region: dto.subscription_price.region,
          taxAmount: dto.subscription_price.tax_amount,
          taxPercentage: dto.subscription_price.tax_percentage,
        } as ModulePrice)
      : undefined,
    fullPaymentPrice: dto.full_payment_price
      ? ({
          priceId: dto.full_payment_price.price_id,
          amount: dto.full_payment_price.amount,
          currency: dto.full_payment_price.currency,
          region: dto.full_payment_price.region,
          taxAmount: dto.full_payment_price.tax_amount,
          taxPercentage: dto.full_payment_price.tax_percentage,
        } as ModulePrice)
      : undefined,
    stats: {
      quizTotal: dto.stats?.quiz_total || 0,
      quizCompleted: dto.stats?.quiz_completed || 0,
      articlesTotal: dto.stats?.articles_total || 0,
      articlesCompleted: dto.stats?.articles_completed || 0,
      lessonTotal: dto.stats?.lesson_total || 0,
      lessonCompleted: dto.stats?.lesson_completed || 0,
      videoCompleted: dto.stats?.video_completed || 0,
      videoTotal: dto.stats?.video_total || 0,
      progress: dto.stats?.progress || 0,
      completed:
        dto.stats?.progress && dto.stats.progress >= 100 ? true : dto.stats?.completed || false,
      failed:
        (dto.stats?.failed === undefined ? dto.quizes?.some((q) => q.failed) : dto.stats?.failed) ||
        false,
      assessmentReport: dto.stats?.assessment_report,
    } as ModuleStats,
    videos:
      dto.videos
        ?.map(
          (v) =>
            ({
              id: v.id,
              title: v.title,
              description: v.description,
              order: v.order,
              url: v.bunny_video_url,
              preview: v.bunny_perview_url,
              completed: v.completed,
            }) as Video,
        )
        .sort((a, b) => a.order - b.order) || [],
    plan: plan
      ? ({
          id: plan.id,
          title: plan.title,
          imageUrl: plan.image_url,
          nextModuleId: plan.next_module_id,
          certificateUrl: plan.certificate_url,
        } as Plan)
      : null,
    order: null,
    planOrder: dto.plan_order ?? null,
    isLocked: !isPlan,
    modules:
      dto.modules
        ?.map((m) => parseModuleDTO(m, plan, isPlan))
        .sort((a, b) => (a.planOrder || 0) - (b.planOrder || 0)) || [],
    playgroundFiles:
      dto.playground_files?.map((f) => ({
        fileName: f.file_name,
        link: f.link,
      })) || [],
    hasTrial: dto.has_trial || false,
    type: dto.payment_type,
    nextPaymentTime: dto.next_payment_time || null,
  };
};

export const parseModulesWithPlans = (modules: ModuleDTO[]): Map<string, Module[]> => {
  const modulesByPlan = new Map<string, Module[]>();

  modules.forEach((dto) => {
    if (!dto.plans?.length) {
      if (!modulesByPlan.has('-1')) {
        modulesByPlan.set('-1', []);
      }

      modulesByPlan.get('-1')?.push(parseModuleDTO(dto));
      return;
    }

    dto.plans.forEach((plan) => {
      if (!modulesByPlan.has(plan.id)) {
        modulesByPlan.set(plan.id, []);
      }

      modulesByPlan.get(plan.id)?.push(parseModuleDTO(dto, plan));
    });
  });

  Array.from(modulesByPlan.entries()).forEach(([planId, planModules]) => {
    if (planId === '-1') {
      return;
    }

    const moduleChain = new Map<string, number>();
    let currentModuleId: string | null = planModules.find((m) => !m.plan?.nextModuleId)?.id || null;
    let order = planModules.length - 1;

    while (currentModuleId) {
      moduleChain.set(currentModuleId, order);
      currentModuleId =
        // eslint-disable-next-line @typescript-eslint/no-loop-func
        planModules.find((m) => m.plan?.nextModuleId === currentModuleId)?.id || null;
      order -= 1;
    }

    planModules.forEach((mod) => {
      // eslint-disable-next-line no-param-reassign
      mod.order = moduleChain.has(mod.id) ? moduleChain.get(mod.id)! : null;
    });

    planModules.forEach((mod) => {
      if (mod.order === null || mod.order === 0) {
        // eslint-disable-next-line no-param-reassign
        mod.isLocked = false;
      } else {
        // eslint-disable-next-line no-param-reassign
        mod.isLocked = !planModules
          .filter((m) => m.order !== null && m.order < mod.order!)
          .every((m) => m.stats.completed);
      }
    });

    planModules.sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
  });

  return modulesByPlan;
};

export const getModulesChainLength = (
  moduleId: string,
  modulesByPlan: Map<string, Module[]>,
  planId?: string,
): number => {
  if (!planId) {
    return 0;
  }

  const modulesInPlan = modulesByPlan.get(planId) || [];
  const visited = new Set<string>();
  let length: number = 0;
  let currentId: string = moduleId;

  while (currentId && !visited.has(currentId)) {
    // eslint-disable-next-line @typescript-eslint/no-loop-func
    const currentModule = modulesInPlan.find((m) => m.id === currentId);

    if (!currentModule?.plan) {
      break;
    }

    length += 1;
    visited.add(currentId);
    currentId = currentModule.plan.nextModuleId || '';
  }

  return length;
};

export const getBestModuleByChainLength = (
  moduleId: string,
  modulesByPlan: Map<string, Module[]>,
): Module | undefined => {
  const chainLengths = new Map<Module, number>();

  Array.from(modulesByPlan.values())
    .flat()
    .filter((m) => m.id === moduleId)
    .forEach((m) => {
      chainLengths.set(m, getModulesChainLength(m.id, modulesByPlan, m.plan?.id));
    });

  if (chainLengths.size === 0) {
    return undefined;
  }

  const bestModule = Array.from(chainLengths.entries()).reduce((best, current) => {
    return current[1] > best[1] ? current : best;
  });

  return bestModule[0];
};

export const getModuleByPlan = (
  moduleId: string,
  planId: string,
  modulesByPlan: Map<string, Module[]>,
): Module | undefined => {
  if (modulesByPlan.has(planId)) {
    const moduleInActivePlan = modulesByPlan.get(planId)?.find((m) => m.id === moduleId);
    return moduleInActivePlan || getBestModuleByChainLength(moduleId, modulesByPlan);
  }

  return getBestModuleByChainLength(moduleId, modulesByPlan);
};
